/**
 * Delivery Report Generator
 *
 * Generates a "proof of delivery" report for a campaign in PDF / Excel / CSV.
 * The Studio downloads the report from JobDetail and forwards to clients.
 *
 * Each generator takes the same inputs:
 *   - job:          the campaign doc (client, campaign, period, stations[], ...)
 *   - deliveries:   array of delivery docs (broadcasterOrgName, status, stations[], acceptedAt, ...)
 *   - sourceOrgName: the studio org sending the deliveries (current org name)
 *
 * Output is downloaded directly to the user's machine via a Blob URL.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface Job {
  id: string;
  client?: string | { label?: string; value?: string };
  campaign?: string;
  period?: string;
  date?: string;
  revision?: string;
  status?: string;
  stations?: any[];
  stationCount?: number;
  linkedCount?: number;
  uploadedAt?: number;
  uploadedByName?: string;
}

interface Delivery {
  id?: string;
  deliveryId?: string;
  deliveryCode?: string;
  broadcasterOrgName?: string;
  broadcasterOrgId?: string | null;
  isMediaHouse?: boolean;
  status?: 'pending' | 'accepted' | 'rejected' | string;
  acceptedAt?: number;
  acceptedByName?: string;
  rejectedAt?: number;
  rejectedByName?: string;
  rejectionReason?: string;
  stations?: any[];
  stationCount?: number;
  trackCount?: number;
  tracksCopied?: number;
  ftpStatus?: string;
  ftpFileCount?: number;
  createdAt?: number;
}

interface ReportContext {
  job: Job;
  deliveries: Delivery[];
  sourceOrgName: string;
}

// ---------- shared helpers ----------

const clientName = (job: Job): string => {
  if (!job.client) return '';
  if (typeof job.client === 'string') return job.client;
  return job.client.label || job.client.value || '';
};

const formatDate = (ts?: number) => (ts ? new Date(ts).toLocaleString() : '');

const fileTimestamp = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
};

const sanitizeFilename = (s: string) =>
  s.replace(/[^a-z0-9-_]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

const reportFilename = ({ job }: ReportContext, ext: string) => {
  const parts = [
    'delivery-report',
    sanitizeFilename(clientName(job) || 'client'),
    sanitizeFilename(job.campaign || 'campaign'),
    fileTimestamp(),
  ];
  return `${parts.join('_')}.${ext}`;
};

// Phase 2 — cascade delete may mark individual stations `deleted: true`
// inside a delivery's stations array, and may mark the whole delivery
// `status: 'deleted'` when every station is gone. Reports live-recompute
// from these docs, so we filter both at read time.
const isLiveStation = (s: any): boolean => !s?.deleted;
const isLiveDelivery = (d: any): boolean => d?.status !== 'deleted';
const liveStations = (d: any): any[] =>
  Array.isArray(d?.stations) ? d.stations.filter(isLiveStation) : [];

/** Counts/totals shown in the summary block. */
function summarize({ deliveries }: ReportContext) {
  const liveDeliveries = deliveries.filter(isLiveDelivery);
  const totalBroadcasters = liveDeliveries.length;
  const accepted = liveDeliveries.filter((d) => d.status === 'accepted').length;
  const rejected = liveDeliveries.filter((d) => d.status === 'rejected').length;
  const pending = liveDeliveries.filter((d) => d.status !== 'accepted' && d.status !== 'rejected').length;
  const totalStations = liveDeliveries.reduce(
    (sum, d) => sum + (Array.isArray(d.stations) ? liveStations(d).length : d.stationCount ?? 0),
    0,
  );
  // Live-read rows are counted inside totalStations (they ARE delivered
  // rows, just without audio). Broken out separately so the summary can
  // render "12 stations (+3 LR)" and the reader knows the totalTracks gap
  // isn't audio missing in action.
  const liveReadStations = liveDeliveries.reduce((sum, d) => {
    return sum + liveStations(d).filter((s: any) => s.isLiveRead).length;
  }, 0);
  const totalTracks = liveDeliveries.reduce((sum, d) => {
    // trackCount / tracksCopied are historical counters stamped at
    // delivery time — they DON'T reflect cascade-deleted stations. Re-
    // derive from the live stations array whenever it's available so
    // deletion is reflected; only fall back to the counters when the
    // array is missing.
    const stations = liveStations(d);
    if (stations.length > 0 || Array.isArray(d.stations)) {
      const ids = new Set(stations.filter((s: any) => s.linkedTrackId).map((s: any) => s.linkedTrackId));
      return sum + ids.size;
    }
    if (typeof d.trackCount === 'number') return sum + d.trackCount;
    if (typeof d.tracksCopied === 'number') return sum + d.tracksCopied;
    return sum;
  }, 0);
  return { totalBroadcasters, accepted, rejected, pending, totalStations, liveReadStations, totalTracks };
}

/** Per-delivery overview rows used in PDF / Excel "Deliveries" tables. */
function deliveryRows({ deliveries }: ReportContext) {
  return deliveries.filter(isLiveDelivery).map((d) => {
    const stations = liveStations(d);
    const hasStationsArray = Array.isArray(d.stations);
    return {
      broadcaster: d.broadcasterOrgName || '',
      type: d.isMediaHouse ? 'Media House' : 'Broadcaster',
      status: (d.status || 'pending').replace(/^./, (c) => c.toUpperCase()),
      stations: hasStationsArray ? stations.length : d.stationCount ?? 0,
      tracks: hasStationsArray
        ? new Set(stations.filter((s: any) => s.linkedTrackId).map((s: any) => s.linkedTrackId)).size
        : typeof d.trackCount === 'number'
        ? d.trackCount
        : typeof d.tracksCopied === 'number'
        ? d.tracksCopied
        : 0,
      ftp: d.ftpStatus ? `${d.ftpStatus}${d.ftpFileCount ? ` (${d.ftpFileCount})` : ''}` : '',
      respondedAt: d.acceptedAt
        ? formatDate(d.acceptedAt)
        : d.rejectedAt
        ? formatDate(d.rejectedAt)
        : '',
      respondedBy: d.acceptedByName || d.rejectedByName || '',
      deliveryCode: d.deliveryCode || '',
      reason: d.status === 'rejected' ? d.rejectionReason || '' : '',
    };
  });
}

/**
 * Normalise a station's language to a display string for the report cells.
 *
 * A station's `language` may be a plain string (job doc) OR — on delivery
 * records, which copy the audiometa `language` field over the job station —
 * an ARRAY of { label, value } option objects (and occasionally a single
 * such object). Dropping that object/array straight into a PDF / Excel / CSV
 * cell renders "[object Object]". This mirrors `languageLabel` in
 * campaignReport.ts so both reports resolve language identically.
 */
const languageLabel = (s: any): string => {
  if (s?.language && typeof s.language === 'string') return s.language;
  if (Array.isArray(s?.language)) {
    return s.language.map((l: any) => (typeof l === 'string' ? l : l?.label)).filter(Boolean).join(', ');
  }
  if (s?.language && typeof s.language === 'object') {
    return String(s.language.label || s.language.value || '').trim();
  }
  if (Array.isArray(s?.languageOptions)) {
    return s.languageOptions.map((o: any) => (typeof o === 'string' ? o : o?.label)).filter(Boolean).join(', ');
  }
  return '';
};

/** Per-station detail rows. Each station is flattened with its broadcaster for cross-reference. */
function stationRows({ deliveries }: ReportContext) {
  const rows: any[] = [];
  for (const d of deliveries) {
    if (!isLiveDelivery(d)) continue;
    if (!Array.isArray(d.stations)) continue;
    for (const s of liveStations(d)) {
      rows.push({
        broadcaster: d.broadcasterOrgName || '',
        station: s.station || '',
        mediaOwner: s.mediaOwner || '',
        language: languageLabel(s),
        duration: s.duration || '',
        flightingCode: s.flightingCode || '',
        deadline: s.materialDeadline || '',
        // Live reads have no audio to deliver — surface that explicitly so
        // the report reader doesn't think an audio row went missing.
        // Preserves any explicit `s.deliveryStatus` (e.g. 'accepted') if
        // the acting org has set one; only fills in the default when it's
        // absent.
        deliveryStatus: s.deliveryStatus || (s.isLiveRead ? 'live_read' : s.linkedTrackId ? 'delivered' : 'pending'),
        broadcasterStatus: (d.status || 'pending').replace(/^./, (c) => c.toUpperCase()),
      });
    }
  }
  return rows;
}

// ---------- PDF ----------

/**
 * Builds the delivery report PDF and returns a Blob — used both by the
 * download button and by the automatic delivery-confirmation email pipeline,
 * which uploads the Blob to Storage and attaches it server-side.
 */
export function buildDeliveryReportPdfBlob(ctx: ReportContext): Blob {
  const doc = renderDeliveryReportPdf(ctx);
  return doc.output('blob');
}

export function deliveryReportFilename(ctx: ReportContext): string {
  return reportFilename(ctx, 'pdf');
}

export function downloadDeliveryReportPdf(ctx: ReportContext) {
  const doc = renderDeliveryReportPdf(ctx);
  doc.save(reportFilename(ctx, 'pdf'));
}

function renderDeliveryReportPdf(ctx: ReportContext): jsPDF {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const { job, deliveries, sourceOrgName } = ctx;
  const summary = summarize(ctx);

  // Brand palette — mirrors src/styles/_tokens.scss (Night-time peach theme)
  const BG_DEEP = [13, 8, 20] as const;        // #0d0814 — deep indigo background
  const ACCENT = [107, 107, 255] as const;     // #6b6bff — primary accent (blue)
  const ACCENT_MUTED = [59, 59, 148] as const; // #3b3b94 — muted accent
  const ALT_ROW = [243, 243, 255] as const;    // very light tint of the accent for stripes

  // Header band — deep indigo with accent-blue wordmark
  doc.setFillColor(BG_DEEP[0], BG_DEEP[1], BG_DEEP[2]);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 60, 'F');
  doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('RADIO VAULT', 40, 30);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Delivery Report', 40, 47);
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 200);
  doc.text(`Generated ${new Date().toLocaleString()}`, doc.internal.pageSize.getWidth() - 40, 47, { align: 'right' });

  // Thin accent rule under the header band
  doc.setDrawColor(ACCENT[0], ACCENT[1], ACCENT[2]);
  doc.setLineWidth(1.5);
  doc.line(0, 61, doc.internal.pageSize.getWidth(), 61);

  // Summary block — accent-coloured section headings, dark body text
  doc.setFontSize(10);
  let y = 90;
  const left = 40;
  const colGap = 280;
  const lh = 16;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(ACCENT_MUTED[0], ACCENT_MUTED[1], ACCENT_MUTED[2]);
  doc.text('Campaign', left, y);
  doc.text('Delivery Summary', left + colGap, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 30, 30);
  y += lh;
  doc.text(`Client: ${clientName(job) || '—'}`, left, y);
  doc.text(`Broadcasters: ${summary.totalBroadcasters}`, left + colGap, y);
  y += lh;
  doc.text(`Campaign: ${job.campaign || '—'}`, left, y);
  doc.text(
    `Accepted: ${summary.accepted}    Rejected: ${summary.rejected}    Pending: ${summary.pending}`,
    left + colGap,
    y,
  );
  y += lh;
  doc.text(`Period: ${job.period || '—'}`, left, y);
  doc.text(
    `Total stations: ${summary.totalStations}${summary.liveReadStations > 0 ? ` (+${summary.liveReadStations} LR)` : ''}`,
    left + colGap,
    y,
  );
  y += lh;
  doc.text(`From: ${sourceOrgName}`, left, y);
  doc.text(`Total tracks: ${summary.totalTracks}`, left + colGap, y);
  y += lh + 10;

  // Per-delivery table — accent-blue header, light-tint alternate rows
  autoTable(doc, {
    startY: y,
    head: [['Broadcaster', 'Type', 'Status', 'Stations', 'Tracks', 'FTP', 'Responded', 'By', 'Delivery Code']],
    body: deliveryRows(ctx).map((r) => [
      r.broadcaster, r.type, r.status, r.stations, r.tracks, r.ftp, r.respondedAt, r.respondedBy, r.deliveryCode,
    ]),
    styles: { fontSize: 8, cellPadding: 4, textColor: [30, 30, 30] },
    headStyles: { fillColor: [ACCENT[0], ACCENT[1], ACCENT[2]], textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [ALT_ROW[0], ALT_ROW[1], ALT_ROW[2]] },
    margin: { left: 40, right: 40 },
  });

  // Per-station table on a fresh page if there are stations
  const rows = stationRows(ctx);
  if (rows.length > 0) {
    doc.addPage();
    doc.setFillColor(BG_DEEP[0], BG_DEEP[1], BG_DEEP[2]);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 36, 'F');
    doc.setDrawColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    doc.setLineWidth(1.5);
    doc.line(0, 37, doc.internal.pageSize.getWidth(), 37);
    doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Station Detail', 40, 24);
    autoTable(doc, {
      startY: 56,
      head: [['Broadcaster', 'Station', 'Media Owner', 'Language', 'Dur.', 'Flighting Code', 'Deadline', 'Station Status', 'Broadcaster Status']],
      body: rows.map((r) => [
        r.broadcaster, r.station, r.mediaOwner, r.language, r.duration,
        r.flightingCode, r.deadline, r.deliveryStatus, r.broadcasterStatus,
      ]),
      styles: { fontSize: 7.5, cellPadding: 3, textColor: [30, 30, 30] },
      headStyles: { fillColor: [ACCENT[0], ACCENT[1], ACCENT[2]], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [ALT_ROW[0], ALT_ROW[1], ALT_ROW[2]] },
      margin: { left: 40, right: 40 },
    });
  }

  // Footer with page numbers
  const totalPages = (doc as any).internal.getNumberOfPages?.() ?? 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 160);
    doc.text(
      `Page ${i} of ${totalPages}`,
      doc.internal.pageSize.getWidth() - 40,
      doc.internal.pageSize.getHeight() - 20,
      { align: 'right' },
    );
  }

  return doc;
}

// ---------- Excel ----------

export function downloadDeliveryReportExcel(ctx: ReportContext) {
  const { job, sourceOrgName } = ctx;
  const summary = summarize(ctx);

  const wb = XLSX.utils.book_new();

  // --- Summary sheet ---
  const summaryAOA: any[][] = [
    ['Radio Vault — Delivery Report'],
    [`Generated ${new Date().toLocaleString()}`],
    [],
    ['Campaign'],
    ['Client', clientName(job) || ''],
    ['Campaign', job.campaign || ''],
    ['Period', job.period || ''],
    ['From (Studio)', sourceOrgName],
    [],
    ['Delivery Summary'],
    ['Broadcasters', summary.totalBroadcasters],
    ['Accepted', summary.accepted],
    ['Rejected', summary.rejected],
    ['Pending', summary.pending],
    [
      'Total stations',
      summary.liveReadStations > 0
        ? `${summary.totalStations} (+${summary.liveReadStations} LR)`
        : summary.totalStations,
    ],
    ['Total tracks', summary.totalTracks],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryAOA);
  summarySheet['!cols'] = [{ wch: 22 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  // --- Deliveries sheet ---
  const deliveries = deliveryRows(ctx).map((r) => ({
    Broadcaster: r.broadcaster,
    Type: r.type,
    Status: r.status,
    Stations: r.stations,
    Tracks: r.tracks,
    FTP: r.ftp,
    Responded: r.respondedAt,
    By: r.respondedBy,
    'Delivery Code': r.deliveryCode,
    'Rejection Reason': r.reason,
  }));
  const deliveriesSheet = XLSX.utils.json_to_sheet(deliveries);
  deliveriesSheet['!cols'] = [
    { wch: 28 }, { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
    { wch: 14 }, { wch: 22 }, { wch: 20 }, { wch: 22 }, { wch: 40 },
  ];
  XLSX.utils.book_append_sheet(wb, deliveriesSheet, 'Deliveries');

  // --- Stations sheet ---
  const stations = stationRows(ctx).map((r) => ({
    Broadcaster: r.broadcaster,
    Station: r.station,
    'Media Owner': r.mediaOwner,
    Language: r.language,
    Duration: r.duration,
    'Flighting Code': r.flightingCode,
    Deadline: r.deadline,
    'Station Status': r.deliveryStatus,
    'Broadcaster Status': r.broadcasterStatus,
  }));
  const stationsSheet = XLSX.utils.json_to_sheet(stations);
  stationsSheet['!cols'] = [
    { wch: 28 }, { wch: 24 }, { wch: 24 }, { wch: 24 }, { wch: 10 },
    { wch: 22 }, { wch: 14 }, { wch: 16 }, { wch: 18 },
  ];
  XLSX.utils.book_append_sheet(wb, stationsSheet, 'Stations');

  XLSX.writeFile(wb, reportFilename(ctx, 'xlsx'));
}

// ---------- CSV ----------

export function downloadDeliveryReportCsv(ctx: ReportContext) {
  const { job, sourceOrgName } = ctx;
  const summary = summarize(ctx);

  const escape = (v: any) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const row = (...vals: any[]) => vals.map(escape).join(',');

  const lines: string[] = [];
  // Header / context block as comment-like rows up top.
  lines.push(row('# Radio Vault Delivery Report'));
  lines.push(row(`# Generated`, new Date().toLocaleString()));
  lines.push(row('# Client', clientName(job)));
  lines.push(row('# Campaign', job.campaign || ''));
  lines.push(row('# Period', job.period || ''));
  lines.push(row('# From (Studio)', sourceOrgName));
  lines.push(row('# Broadcasters', summary.totalBroadcasters));
  lines.push(row('# Accepted', summary.accepted));
  lines.push(row('# Rejected', summary.rejected));
  lines.push(row('# Pending', summary.pending));
  lines.push(row(
    '# Total stations',
    summary.liveReadStations > 0
      ? `${summary.totalStations} (+${summary.liveReadStations} LR)`
      : summary.totalStations,
  ));
  lines.push(row('# Total tracks', summary.totalTracks));
  lines.push('');

  // Per-station table — flat, the most useful single-sheet view.
  lines.push(row(
    'Broadcaster', 'Station', 'Media Owner', 'Language', 'Duration',
    'Flighting Code', 'Deadline', 'Station Status', 'Broadcaster Status',
  ));
  for (const r of stationRows(ctx)) {
    lines.push(row(
      r.broadcaster, r.station, r.mediaOwner, r.language, r.duration,
      r.flightingCode, r.deadline, r.deliveryStatus, r.broadcasterStatus,
    ));
  }

  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = reportFilename(ctx, 'csv');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
